import type { ReactNode } from 'react'
import {
  IconCircleCheck,
  IconAlertTriangle,
  IconAlertOctagon,
  IconCircleX,
  IconAlertCircle,
  IconInfoCircle,
  IconArrowUpRight,
  IconArrowDownRight,
  IconMinus,
} from '@tabler/icons-react'
import { deltaTone, STATUS_LABELS, SEVERITY_LABELS } from '../types'
import type { StatusRole, SeverityLevel, DeltaPolarity } from '../types'

/* Status / severity / metric indicators. Exports only components. */

function statusIcon(role: StatusRole, size: number) {
  switch (role) {
    case 'good': return <IconCircleCheck size={size} stroke={2} />
    case 'warning': return <IconAlertTriangle size={size} stroke={2} />
    case 'serious': return <IconAlertOctagon size={size} stroke={2} />
    case 'critical': return <IconCircleX size={size} stroke={2} />
  }
}

function severityIcon(level: SeverityLevel, size: number) {
  switch (level) {
    case 'critical': return <IconCircleX size={size} stroke={2} />
    case 'high': return <IconAlertTriangle size={size} stroke={2} />
    case 'medium': return <IconAlertCircle size={size} stroke={2} />
    case 'low': return <IconInfoCircle size={size} stroke={2} />
  }
}

/**
 * Health / execution status. ALWAYS renders an icon plus a text label — status
 * colour never carries meaning alone, and on the light surface `warning` and
 * `serious` are deliberately below 3:1 with the pairing as the mitigation.
 */
export function StatusBadge({ status, label }: { status: StatusRole; label?: ReactNode }) {
  return (
    <span className="lh-badge" data-status={status}>
      {statusIcon(status, 12)}
      {label ?? STATUS_LABELS[status]}
    </span>
  )
}

/**
 * Signal severity. Rendered as an outlined tag rather than a tinted pill so it
 * stays visually distinct from {@link StatusBadge} — the two appear side by side
 * on Overview and must not read as the same scale.
 */
export function SeverityBadge({ severity, label }: { severity: SeverityLevel; label?: ReactNode }) {
  return (
    <span className="lh-badge" data-severity={severity}>
      {severityIcon(severity, 11)}
      {label ?? SEVERITY_LABELS[severity]}
    </span>
  )
}

/** A service row: icon, name, status. Rows self-separate with a hairline. */
export function HealthRow({ name, status, statusLabel, icon }: {
  name: ReactNode
  status: StatusRole
  statusLabel?: ReactNode
  icon?: ReactNode
}) {
  return (
    <div className="lh-health-row">
      {icon && <span className="lh-health-icon">{icon}</span>}
      <span className="lh-health-name">{name}</span>
      <StatusBadge status={status} label={statusLabel} />
    </div>
  )
}

function formatDelta(change: number, label?: string) {
  if (label) return label
  const abs = Math.abs(change)
  // Keep small changes meaningful (0.03% matters for a success rate) without
  // printing noise on large ones.
  const digits = abs < 1 ? 2 : 1
  return `${abs.toFixed(digits)}%`
}

/**
 * A signed change indicator.
 *
 * The ARROW follows the raw sign; the COLOUR follows intent. `polarity` is
 * required precisely because tone cannot be inferred from the sign: a rise in
 * failed records is adverse, a rise in records processed is favourable.
 */
export function Delta({ change, polarity, label }: {
  change: number
  polarity: DeltaPolarity
  label?: string
}) {
  const tone = deltaTone(change, polarity)
  const Arrow = change > 0 ? IconArrowUpRight : change < 0 ? IconArrowDownRight : IconMinus
  return (
    <span className="lh-delta" data-tone={tone}>
      <Arrow size={12} stroke={2.5} />
      {formatDelta(change, label)}
    </span>
  )
}

interface StatTileBase {
  label: ReactNode
  value: ReactNode
  size?: 'sm' | 'md' | 'lg'
  sublabel?: ReactNode
  /** Optional status pill beside the value, e.g. a health KPI. */
  status?: StatusRole
}

/**
 * Discriminated so `delta` cannot be supplied without `deltaPolarity` — the
 * favourable/adverse bug class is prevented at compile time rather than by
 * reviewer discipline.
 */
type StatTileProps =
  | (StatTileBase & { delta?: undefined; deltaPolarity?: never; deltaLabel?: never })
  | (StatTileBase & { delta: number; deltaPolarity: DeltaPolarity; deltaLabel?: string })

/** KPI tile: label, big value, optional delta and sublabel. */
export function StatTile(props: StatTileProps) {
  const { label, value, size = 'md', sublabel, status } = props
  return (
    <div className="lh-stat-tile">
      <span className="lh-stat-label">{label}</span>
      <span className="lh-stat-value" data-size={size}>{value}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {props.delta !== undefined && (
          <Delta change={props.delta} polarity={props.deltaPolarity} label={props.deltaLabel} />
        )}
        {status && <StatusBadge status={status} />}
        {sublabel && <span className="lh-stat-sublabel">{sublabel}</span>}
      </span>
    </div>
  )
}
